import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ConversationFilters, DateFilter, Sort, TimeWindow } from '../core/api/query.js';
import type { Sentiment } from '../core/api/types.js';

export type ConversationSortField = 'StartedAt' | 'Duration' | 'Sentiment' | 'AssignedAt';

/**
 * Filters and sort, held in the URL.
 *
 * This is the one genuinely web-specific resume requirement: a portal user emailing a
 * colleague "look at this" should send a link that reproduces what they were looking at.
 * It also means back and forward work, a reload keeps the view, and a screen-reader user does
 * not have to rebuild state they already built once.
 *
 * The three date modes are mutually exclusive - sending two is a 400 - so they are stored as
 * ONE `date` parameter with a mode prefix rather than as separate fields that could both be
 * set. The invalid combination is unrepresentable rather than validated after the fact.
 */
export interface QueryState {
  readonly filters: ConversationFilters;
  readonly sort: Sort<ConversationSortField>;
  readonly setFilters: (next: ConversationFilters) => void;
  readonly setSort: (next: Sort<ConversationSortField>) => void;
  readonly clear: () => void;
  readonly activeCount: number;
}

const SORT_FIELDS: readonly ConversationSortField[] = ['StartedAt', 'Duration', 'Sentiment', 'AssignedAt'];
const SENTIMENTS: readonly Sentiment[] = ['Positive', 'Neutral', 'Negative'];

export function useQueryState(): QueryState {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<ConversationFilters>(() => {
    const f: Record<string, unknown> = {};
    const sentiment = params.get('sentiment');
    if (sentiment && (SENTIMENTS as readonly string[]).includes(sentiment)) f.sentiment = sentiment;
    const outcome = params.get('outcome');
    if (outcome) f.outcome = outcome;
    const agentId = params.get('agentId');
    if (agentId) f.agentId = agentId;
    const clientId = params.get('clientId');
    if (clientId) f.clientId = clientId;
    const assignedUserId = params.get('assignedUserId');
    if (assignedUserId) f.assignedUserId = assignedUserId;
    if (params.get('unassignedOnly') === '1') f.unassignedOnly = true;

    const date = parseDate(params.get('date'));
    if (date) f.date = date;
    const window = parseWindow(params.get('time'));
    if (window) f.timeWindow = window;
    return f as ConversationFilters;
  }, [params]);

  const sort = useMemo<Sort<ConversationSortField>>(() => {
    const by = params.get('sortBy');
    const dir = params.get('sortDir');
    return {
      sortBy: (by && (SORT_FIELDS as readonly string[]).includes(by) ? by : 'StartedAt') as ConversationSortField,
      sortDirection: dir === 'Asc' ? 'Asc' : 'Desc',
    };
  }, [params]);

  const write = useCallback((f: ConversationFilters, s: Sort<ConversationSortField>) => {
    const p = new URLSearchParams();
    if (f.sentiment) p.set('sentiment', f.sentiment);
    if (f.outcome) p.set('outcome', f.outcome);
    if (f.agentId) p.set('agentId', f.agentId);
    if (f.clientId) p.set('clientId', f.clientId);
    if (f.assignedUserId) p.set('assignedUserId', f.assignedUserId);
    if (f.unassignedOnly) p.set('unassignedOnly', '1');
    if (f.date) p.set('date', serialiseDate(f.date));
    if (f.timeWindow) p.set('time', `${f.timeWindow.fromTime}-${f.timeWindow.toTime}`);
    if (s.sortBy !== 'StartedAt') p.set('sortBy', s.sortBy);
    if (s.sortDirection !== 'Desc') p.set('sortDir', s.sortDirection);
    // replace, not push: changing a filter is refining one view, not navigating to a new
    // place. Pushing would make Back step through every keystroke of a text filter.
    setParams(p, { replace: true });
  }, [setParams]);

  const activeCount = [
    filters.sentiment, filters.outcome, filters.agentId, filters.clientId,
    filters.assignedUserId, filters.unassignedOnly ? '1' : undefined,
    filters.date ? '1' : undefined, filters.timeWindow ? '1' : undefined,
  ].filter(Boolean).length;

  return {
    filters, sort, activeCount,
    setFilters: (next) => write(next, sort),
    setSort: (next) => write(filters, next),
    clear: () => write({}, { sortBy: 'StartedAt', sortDirection: 'Desc' }),
  };
}

/** `day:2026-09-05` | `range:2026-09-01..2026-09-05` — one field, so two modes cannot coexist. */
function parseDate(raw: string | null): DateFilter | undefined {
  if (!raw) return undefined;
  const [mode, rest] = raw.split(':', 2);
  if (mode === 'day' && rest) return { mode: 'day', date: rest };
  if (mode === 'range' && rest?.includes('..')) {
    const [from, to] = rest.split('..');
    if (from && to) return { mode: 'dayRange', fromDate: from, toDate: to };
  }
  return undefined;
}

function serialiseDate(d: DateFilter): string {
  if (d.mode === 'day') return `day:${d.date}`;
  if (d.mode === 'dayRange') return `range:${d.fromDate}..${d.toDate}`;
  return `instants:${d.from}..${d.to}`;
}

function parseWindow(raw: string | null): TimeWindow | undefined {
  if (!raw) return undefined;
  const [fromTime, toTime] = raw.split('-');
  if (!fromTime || !toTime) return undefined;
  if (fromTime === toTime) return undefined;
  return { fromTime, toTime };
}
